import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Bot,
  BrainCircuit,
  CheckCircle2,
  CircleStop,
  Clock3,
  Database,
  Download,
  ExternalLink,
  FileText,
  GitBranch,
  Gauge,
  History,
  ListChecks,
  LoaderCircle,
  MessageSquareText,
  Network,
  Play,
  Radio,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  TerminalSquare,
  UserRound,
  Wrench,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { toast } from '../hooks/use-toast';
import { Progress } from './ui/progress';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { NativeResponseActions, type NativeResponseActionId } from './NativeResponseActions';

interface AiC2Status {
  ok: boolean;
  projectEndpoint: string;
  agentName: string;
  agents?: Array<{ tier: 'worker' | 'analyst' | 'expert'; label: string; model: string; deployment: string; agentName: string; pricing?: { inputUsdPerMillion?: number; cachedInputUsdPerMillion?: number; outputUsdPerMillion?: number } }>;
  authentication: string;
  secretsRequired: boolean;
  stage: string;
  operatingMode: string;
  toolsEnabled: number;
  reasoningControl?: string;
  toolRuntime?: string;
  retryPolicy?: string;
  costTelemetry?: string;
  toolCatalog?: Array<{ name: string; label: string; capability: string; available: boolean }>;
  reachable?: boolean;
  probes?: Array<{ ok?: boolean; tier?: 'worker' | 'analyst' | 'expert'; latencyMs?: number; retries?: number; message?: string; agent?: Record<string, unknown> }>;
  message?: string;
  stage2CEnabled?: boolean;
  stage2CExportsEnabled?: boolean;
}

interface AiC2TierMetric {
  tier: 'worker' | 'analyst' | 'expert';
  label?: string;
  model?: string;
  deployment?: string;
  agentName?: string;
  calls?: number;
  retries?: number;
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  estimatedUsd?: number;
}

interface AiC2Metrics {
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  reasoningTokens?: number;
  latencyMs?: number;
  toolCalls?: number;
  toolsAvailable?: number;
  agentCalls?: number;
  retries?: number;
  compactions?: number;
  schemaRepairs?: number;
  duplicateToolSuppressions?: number;
  sourceCapabilitiesDisabled?: number;
  workerCheckpoints?: number;
  noProgressCheckpoints?: number;
  degradedCompletion?: boolean;
  disposition?: 'success' | 'partial_success' | 'telemetry_limited' | 'tool_limited' | 'model_fallback' | 'fuse_terminated';
  evidenceLedgerPages?: number;
  evidenceLedgerRows?: number;
  evidenceLedgerTruncations?: number;
  schemaCacheHits?: number;
  complianceRepairs?: number;
  toolFailures?: number;
  fuseRecovered?: boolean;
  estimatedCostUsd?: number;
  finalTier?: 'worker' | 'analyst' | 'expert';
  tiers?: AiC2TierMetric[];
}

interface AiC2Message {
  id: string;
  sequence: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  responseId?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string | null;
}

interface AiC2Event {
  id?: string;
  eventType: string;
  label: string;
  detail?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string | null;
}

interface AiC2Evidence {
  id: string;
  evidenceType: string;
  source: string;
  label: string;
  payload: Record<string, unknown>;
  createdAt?: string | null;
}

interface AiC2MissionSummary {
  id: string;
  customerId: string;
  customerName: string;
  createdByEmail?: string | null;
  operatingLevel: 'l1' | 'l2' | 'l3';
  reasoningDepth: 'standard' | 'deep' | 'maximum';
  missionMode: 'context' | 'autonomous';
  title: string;
  status: string;
  fobSnapshotId?: string | null;
  foundryResponseId?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  latencyMs?: number | null;
  usage?: Record<string, unknown>;
  metrics?: AiC2Metrics;
  errorMessage?: string | null;
  createdAt?: string | null;
}

interface AiC2ToolCall {
  id: string;
  callRef: string;
  toolName: string;
  arguments: Record<string, unknown>;
  reason?: string | null;
  status: string;
  resultPayload?: Record<string, unknown>;
  resultPreview?: string | null;
  durationMs?: number | null;
  requestedAt?: string | null;
  completedAt?: string | null;
}

interface AiC2Mission extends AiC2MissionSummary {
  initialPrompt: string;
  foundryAgentName: string;
  messages: AiC2Message[];
  events: AiC2Event[];
  evidence: AiC2Evidence[];
  toolCalls: AiC2ToolCall[];
}

interface AiC2WorkspaceTimelineItem {
  time: string;
  sortKey: number;
  text: string;
  category: 'compromise' | 'execution' | 'network' | 'identity' | 'discovery' | 'telemetry' | 'other';
}

interface AiC2WorkspaceHypothesis {
  id: string;
  title: string;
  assessment: string;
  status: 'confirmed' | 'likely' | 'investigating' | 'weakened' | 'rejected' | 'unknown';
  supportingEvidence: string[];
  limitations: string[];
}

interface AiC2WorkspaceEntity {
  id: string;
  type: 'device' | 'ip' | 'domain' | 'file' | 'account' | 'other';
  value: string;
  status: 'confirmed_compromise' | 'observed' | 'investigating' | 'indicator' | 'unconfirmed';
  context: string;
}

interface AiC2Workspace {
  missionId: string;
  caseRef: string;
  title: string;
  customerId: string;
  customerName: string;
  state: 'INVESTIGATING' | 'ADJUDICATED' | 'EVIDENCE LIMITED' | 'CANCELLED' | 'FAILED' | 'QUEUED';
  disposition: string;
  createdByEmail?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  elapsedMs: number;
  counts: { queries: number; toolCalls: number; evidenceObjects: number; entities: number; hypotheses: number };
  timeline: AiC2WorkspaceTimelineItem[];
  hypotheses: AiC2WorkspaceHypothesis[];
  rca: { status: 'resolved' | 'unresolved' | 'partial'; conclusion: string };
  attackStory: string[];
  mitre: Array<{ technique: string; name: string; basis: string; confidence: string }>;
  affectedEntities: AiC2WorkspaceEntity[];
  blastRadius: { status: string; summary: string };
  evidenceGaps: string[];
  confidence: Array<{ assessment: string; confidence: string }>;
  recommendedActions: Array<{ priority: number; title: string; steps: string[] }>;
  sourceOutputPresent: boolean;
  fobOverlay: {
    available: boolean;
    snapshotId?: string | null;
    matchedNodeCount: number;
    missionEntityCount: number;
    nodes: Array<{ key: string; label: string; type: string; status: AiC2WorkspaceEntity['status']; riskScore: number; matchedFobNodeKey?: string | null; sourceTables?: string[] }>;
    edges: Array<{ key: string; from: string; to: string; label: string; type: string; confidence: number; evidenceCount: number }>;
  };
}

interface ResponseCommanderProposal {
  id: string; runId: string; missionId: string; actionId: NativeResponseActionId; actionLabel: string; provider: string; riskClass: string;
  status: 'PROPOSED'|'ACCEPTED_FOR_PREFLIGHT'|'REJECTED'; targetEntityType: string; targetEntityValue: string; rationale: string; expectedOutcome: string; confidence: string;
  inputs: Record<string, unknown>; missingRequiredInputs: string[]; evidenceBasis: string[]; warnings: string[]; contractVersion: string; resolverVersion: string; reviewedByEmail?: string|null; reviewedAt?: string|null; handoffAt?: string|null; createdAt: string;
}

interface ResponseCommanderHandoff {
  actionId: NativeResponseActionId; entityType: string; entityValue: string; source: 'aic2'; mobSnapshotId?: string; mobNodeKey?: string; parameters: Record<string, any>; justification: string; missingRequiredInputs: string[]; currentContractVersion: string; currentResolverVersion: string;
}

interface ResponseGuardEvaluation {
  id: string; proposalId: string; missionId: string; customerId: string; actionId: string; targetEntityType: string; targetEntityValue: string; riskClass: string;
  guardStatus: 'READY_FOR_HANDOFF'|'HUMAN_REVIEW_REQUIRED'|'BLOCKED'; policyDecision: 'ALLOW'|'REQUIRE_HUMAN_REVIEW'|'BLOCK'; reasonCodes: string[]; reasons: string[];
  protectedTarget: boolean; matchedProtectionPatterns: string[]; policyFingerprint: string; contractVersion: string; resolverVersion: string;
  judgeVerdict: 'APPROVE'|'HUMAN_REVIEW'|'REJECT'; judgeConfidence: 'low'|'medium'|'high'; judgeSummary: string; judgeReasoning: string[]; contradictoryEvidence: string[];
  evidenceSufficiency: string; proportionality: string; diagnostics: string[]; modelTier: string; modelAgent: string; estimatedCostUsd: number;
  reviewStatus: 'NOT_REQUIRED'|'PENDING'|'APPROVED'|'DENIED'|'NOT_ALLOWED'; reviewedByEmail?: string|null; reviewNote?: string|null; reviewedAt?: string|null;
  expiresAt: string; handoffAt?: string|null; createdAt: string;
}

interface AiCommandControlCenterTabProps {
  onNavigate?: (tabId: string, customerId?: string) => void;
}

type OperatingLevel = 'l1' | 'l2' | 'l3';
type ReasoningDepth = 'standard' | 'deep' | 'maximum';
type MissionMode = 'context' | 'autonomous';

type AiC2ProgressState = { percent: number; phase: string; detail: string };

type LiveStreamState = {
  missionId: string | null;
  output: string;
  activity: AiC2Event[];
  status: 'idle' | 'streaming' | 'completed' | 'cancelled' | 'failed';
  error: string | null;
  metrics: AiC2Metrics | null;
  progress: AiC2ProgressState;
};

const EMPTY_STREAM: LiveStreamState = {
  missionId: null,
  output: '',
  activity: [],
  status: 'idle',
  error: null,
  metrics: null,
  progress: { percent: 0, phase: 'Standing by', detail: 'Launch a mission to begin investigation.' },
};

const LEVELS: Array<{ id: OperatingLevel; label: string; caption: string }> = [
  { id: 'l1', label: 'SOC L1', caption: 'Triage & escalation' },
  { id: 'l2', label: 'SOC L2', caption: 'Correlation & investigation' },
  { id: 'l3', label: 'SOC L3', caption: 'Hypothesis-driven analysis' },
];

const REASONING: Array<{ id: ReasoningDepth; label: string; profile: string; caption: string }> = [
  { id: 'standard', label: 'Standard', profile: 'cost-optimized', caption: 'Luna-first routing' },
  { id: 'deep', label: 'Deep', profile: 'router-guided', caption: 'Terra-eligible investigation' },
  { id: 'maximum', label: 'Maximum', profile: 'expert-eligible', caption: 'Sol only when justified' },
];

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : value;
}

function statusClass(status: string): string {
  if (status === 'completed') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300';
  if (status === 'running' || status === 'queued') return 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-500/10 dark:text-cyan-300';
  if (status === 'cancelled') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300';
  if (status === 'failed') return 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-300';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300';
}

function prettyJson(value: unknown): string {
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  if (minutes) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

function workspaceStateClass(state: AiC2Workspace['state']): string {
  if (state === 'ADJUDICATED') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300';
  if (state === 'INVESTIGATING' || state === 'QUEUED') return 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-500/10 dark:text-cyan-300';
  if (state === 'EVIDENCE LIMITED') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300';
  if (state === 'FAILED') return 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-300';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300';
}

function hypothesisClass(status: AiC2WorkspaceHypothesis['status']): string {
  if (status === 'confirmed') return 'border-emerald-300 text-emerald-700 dark:border-emerald-500/35 dark:text-emerald-300';
  if (status === 'likely') return 'border-cyan-300 text-cyan-700 dark:border-cyan-500/35 dark:text-cyan-300';
  if (status === 'rejected') return 'border-rose-300 text-rose-700 dark:border-rose-500/35 dark:text-rose-300';
  if (status === 'weakened') return 'border-amber-300 text-amber-700 dark:border-amber-500/35 dark:text-amber-300';
  return 'border-violet-300 text-violet-700 dark:border-violet-500/35 dark:text-violet-300';
}

function entityClass(status: AiC2WorkspaceEntity['status']): string {
  if (status === 'confirmed_compromise') return 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200';
  if (status === 'indicator') return 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200';
  if (status === 'investigating') return 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-200';
  if (status === 'observed') return 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/8 dark:text-cyan-200';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300';
}

function commanderEntityType(entity: AiC2WorkspaceEntity): 'device'|'user'|'ip'|'domain'|'hash'|null {
  if (entity.type === 'device') return 'device';
  if (entity.type === 'account') return 'user';
  if (entity.type === 'ip') return 'ip';
  if (entity.type === 'domain') return 'domain';
  if (entity.type === 'file' && /^(?:[a-f0-9]{32}|[a-f0-9]{40}|[a-f0-9]{64})$/i.test(entity.value.trim())) return 'hash';
  return null;
}

async function parseSseResponse(
  response: Response,
  onEvent: (eventName: string, data: Record<string, unknown>) => void,
): Promise<void> {
  if (!response.ok) {
    const text = await response.text();
    let message = text || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      message = String(parsed['message'] ?? parsed['error'] ?? message);
    } catch { /* text is enough */ }
    throw new Error(message);
  }
  if (!response.body) throw new Error('AI C2 stream returned no response body.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const consume = (frame: string) => {
    let name = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split(/\r?\n/)) {
      if (line.startsWith('event:')) name = line.slice(6).trim();
      if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) return;
    try {
      onEvent(name, JSON.parse(dataLines.join('\n')) as Record<string, unknown>);
    } catch {
      onEvent(name, { raw: dataLines.join('\n') });
    }
  };
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let match = buffer.match(/\r?\n\r?\n/);
    while (match && match.index != null) {
      const frame = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      if (frame.trim()) consume(frame);
      match = buffer.match(/\r?\n\r?\n/);
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) consume(buffer);
}

export function AiCommandControlCenterTab({ onNavigate }: AiCommandControlCenterTabProps) {
  const { user, canCapability } = useAuth();
  const { customers } = useCustomers();
  const [customerId, setCustomerId] = useState('');
  const [operatingLevel, setOperatingLevel] = useState<OperatingLevel>('l1');
  const [reasoningDepth, setReasoningDepth] = useState<ReasoningDepth>('standard');
  const [missionMode, setMissionMode] = useState<MissionMode>('context');
  const [prompt, setPrompt] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [status, setStatus] = useState<AiC2Status | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [history, setHistory] = useState<AiC2MissionSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedMission, setSelectedMission] = useState<AiC2Mission | null>(null);
  const [workspace, setWorkspace] = useState<AiC2Workspace | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [commanderTargetKey, setCommanderTargetKey] = useState('');
  const [commanderProposals, setCommanderProposals] = useState<ResponseCommanderProposal[]>([]);
  const [commanderLoading, setCommanderLoading] = useState(false);
  const [commanderError, setCommanderError] = useState('');
  const [commanderDiagnostics, setCommanderDiagnostics] = useState<string[]>([]);
  const [commanderHandoff, setCommanderHandoff] = useState<{ proposalId: string; handoff: ResponseCommanderHandoff } | null>(null);
  const [commanderGuards, setCommanderGuards] = useState<Record<string, ResponseGuardEvaluation>>({});
  const [guardBusy, setGuardBusy] = useState<string | null>(null);
  const [guardFeatureUnavailable, setGuardFeatureUnavailable] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stream, setStream] = useState<LiveStreamState>(EMPTY_STREAM);
  const streamAbortRef = useRef<AbortController | null>(null);
  const outputEndRef = useRef<HTMLDivElement | null>(null);
  const liveMissionIdRef = useRef<string | null>(null);
  const completionToastRef = useRef<string | null>(null);

  const realCustomers = useMemo(() => customers.filter((c) => c.id !== 'default' && c.enabled !== false), [customers]);
  const selectedCustomer = useMemo(() => realCustomers.find((c) => c.id === customerId), [realCustomers, customerId]);
  const commanderTargets = useMemo(() => (workspace?.affectedEntities ?? []).map((entity) => { const responseType = commanderEntityType(entity); return responseType ? { key: `${responseType}|${entity.value}`, responseType, entity } : null; }).filter((item): item is { key:string; responseType:'device'|'user'|'ip'|'domain'|'hash'; entity:AiC2WorkspaceEntity } => Boolean(item)), [workspace]);
  const commanderTarget = useMemo(() => commanderTargets.find((item) => item.key === commanderTargetKey) ?? commanderTargets[0] ?? null, [commanderTargets, commanderTargetKey]);
  const canViewCommander = canCapability('ai_command_control', 'view_response_commander');
  const canGenerateCommander = canCapability('ai_command_control', 'generate_response_commander_proposals');
  const canReviewCommander = canCapability('ai_command_control', 'review_response_commander_proposals');
  const canHandoffCommander = canCapability('ai_command_control', 'handoff_response_commander_proposals');
  const canViewGuard = canCapability('ai_command_control', 'view_response_guard');
  const canEvaluateGuard = canCapability('ai_command_control', 'evaluate_response_guard');
  const canReviewGuard = canCapability('ai_command_control', 'review_response_guard');

  const canLevel = useCallback((level: OperatingLevel) => canCapability('ai_command_control', `launch_${level}_mission`), [canCapability]);
  const canReasoning = useCallback((depth: ReasoningDepth) => {
    if (depth === 'deep') return canCapability('ai_command_control', 'use_deep_reasoning');
    if (depth === 'maximum') return canCapability('ai_command_control', 'use_max_reasoning');
    return true;
  }, [canCapability]);
  const canAutonomous = canCapability('ai_command_control', 'use_readonly_toolbelt');

  useEffect(() => {
    setCommanderTargetKey(commanderTargets[0]?.key ?? ''); setCommanderProposals([]); setCommanderDiagnostics([]); setCommanderError(''); setCommanderHandoff(null); setCommanderGuards({}); setGuardFeatureUnavailable(false);
  }, [workspace?.missionId]);

  const loadCommanderGuard = useCallback(async (proposalId: string) => {
    if (!customerId || !canViewGuard) return null;
    try {
      const data = await apiFetch<{ guard?: ResponseGuardEvaluation|null }>(`/response-commander/proposals/${encodeURIComponent(proposalId)}/guard?customerId=${encodeURIComponent(customerId)}`);
      setGuardFeatureUnavailable(false);
      if (data.guard) setCommanderGuards((current) => ({ ...current, [proposalId]: data.guard as ResponseGuardEvaluation }));
      else setCommanderGuards((current) => { const next = { ...current }; delete next[proposalId]; return next; });
      return data.guard ?? null;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (/Stage 4|feature_disabled|disabled in Feature Flags/i.test(message)) setGuardFeatureUnavailable(true);
      return null;
    }
  }, [customerId, canViewGuard]);

  const loadCommanderProposals = useCallback(async () => {
    if (!selectedMission || !customerId || !commanderTarget || !canViewCommander) { setCommanderProposals([]); return; }
    try {
      const qs = new URLSearchParams({ customerId, targetEntityValue: commanderTarget.entity.value });
      const data = await apiFetch<{ proposals?: ResponseCommanderProposal[] }>(`/response-commander/missions/${encodeURIComponent(selectedMission.id)}/proposals?${qs.toString()}`);
      const rows = Array.isArray(data.proposals) ? data.proposals : [];
      setCommanderProposals(rows);
      if (canViewGuard) await Promise.all(rows.filter((proposal) => proposal.status === 'ACCEPTED_FOR_PREFLIGHT').slice(0,12).map((proposal) => loadCommanderGuard(proposal.id)));
    } catch (error) { setCommanderError(error instanceof Error ? error.message : String(error)); }
  }, [selectedMission?.id, customerId, commanderTarget?.key, canViewCommander, canViewGuard, loadCommanderGuard]);

  useEffect(() => { setCommanderHandoff(null); setCommanderDiagnostics([]); setCommanderError(''); setCommanderGuards({}); void loadCommanderProposals(); }, [loadCommanderProposals]);

  async function generateCommanderProposals() {
    if (!selectedMission || !commanderTarget) return;
    setCommanderLoading(true); setCommanderError(''); setCommanderDiagnostics([]); setCommanderHandoff(null);
    try {
      const data = await apiFetch<{ proposals?: ResponseCommanderProposal[]; diagnostics?: string[] }>(`/response-commander/missions/${encodeURIComponent(selectedMission.id)}/proposals`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ customerId, entityType: commanderTarget.responseType, entityValue: commanderTarget.entity.value }) });
      setCommanderProposals(Array.isArray(data.proposals) ? data.proposals : []); setCommanderDiagnostics(Array.isArray(data.diagnostics) ? data.diagnostics : []);
      toast({ title:'Response Commander proposals generated', description:`${Array.isArray(data.proposals) ? data.proposals.length : 0} contract-bound proposal(s) · no action executed.` });
    } catch (error) { setCommanderError(error instanceof Error ? error.message : String(error)); toast({ title:'Response Commander unavailable', description:error instanceof Error ? error.message : String(error), variant:'destructive' }); }
    finally { setCommanderLoading(false); }
  }

  async function reviewCommanderProposal(proposal: ResponseCommanderProposal, decision: 'accepted_for_preflight'|'rejected') {
    try {
      const data = await apiFetch<{ proposal: ResponseCommanderProposal }>(`/response-commander/proposals/${encodeURIComponent(proposal.id)}/review`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ customerId, decision }) });
      setCommanderProposals((rows) => rows.map((row) => row.id === proposal.id ? data.proposal : row)); setCommanderHandoff(null);
      setCommanderGuards((current) => { const next = { ...current }; delete next[proposal.id]; return next; });
      toast({ title: decision === 'accepted_for_preflight' ? 'Proposal accepted for preflight' : 'Proposal rejected', description: decision === 'accepted_for_preflight' ? 'No action executed. Stage 4 Judge + guard must clear the handoff next.' : 'No response action has been executed.' });
    } catch (error) { toast({ title:'Proposal review failed', description:error instanceof Error ? error.message : String(error), variant:'destructive' }); }
  }

  async function evaluateCommanderGuard(proposal: ResponseCommanderProposal) {
    setGuardBusy(`evaluate:${proposal.id}`); setCommanderHandoff(null);
    try {
      const data = await apiFetch<{ guard: ResponseGuardEvaluation }>(`/response-commander/proposals/${encodeURIComponent(proposal.id)}/guard/evaluate`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ customerId }) });
      setCommanderGuards((current) => ({ ...current, [proposal.id]: data.guard })); setGuardFeatureUnavailable(false);
      const label = data.guard.guardStatus.replaceAll('_',' ');
      toast({ title:`Stage 4 guard: ${label}`, description:`Judge ${data.guard.judgeVerdict} · ${data.guard.judgeConfidence} confidence · no action executed.` });
    } catch (error) { toast({ title:'Stage 4 evaluation failed', description:error instanceof Error ? error.message : String(error), variant:'destructive' }); }
    finally { setGuardBusy(null); }
  }

  async function reviewCommanderGuard(guard: ResponseGuardEvaluation, decision: 'approved'|'denied') {
    setGuardBusy(`review:${guard.id}`); setCommanderHandoff(null);
    try {
      const data = await apiFetch<{ guard: ResponseGuardEvaluation }>(`/response-commander/guards/${encodeURIComponent(guard.id)}/review`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ customerId, decision }) });
      setCommanderGuards((current) => ({ ...current, [guard.proposalId]: data.guard }));
      toast({ title: decision === 'approved' ? 'Stage 4 human guard approved' : 'Stage 4 human guard denied', description:'This is a guard decision only; no response action has executed.' });
    } catch (error) { toast({ title:'Stage 4 guard review failed', description:error instanceof Error ? error.message : String(error), variant:'destructive' }); }
    finally { setGuardBusy(null); }
  }

  async function openCommanderHandoff(proposal: ResponseCommanderProposal) {
    try {
      const data = await apiFetch<{ handoff: ResponseCommanderHandoff }>(`/response-commander/proposals/${encodeURIComponent(proposal.id)}/handoff`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ customerId }) });
      setCommanderHandoff({ proposalId: proposal.id, handoff: data.handoff });
      toast({ title:'Guarded Native Response handoff prepared', description:'Current contract and Stage 4 guard revalidated. Stage 3B.5 live preflight is still required.' });
    } catch (error) { toast({ title:'Response handoff blocked', description:error instanceof Error ? error.message : String(error), variant:'destructive' }); }
  }

  const loadStatus = useCallback(async (probe = false) => {
    if (!customerId) { setStatus(null); return; }
    setStatusLoading(true);
    try {
      const data = await apiFetch<AiC2Status>(`/ai-command-control/status?customerId=${encodeURIComponent(customerId)}${probe ? '&probe=true' : ''}`);
      setStatus(data);
    } catch (error) {
      setStatus({
        ok: false,
        projectEndpoint: '',
        agentName: 'kloudsecure-ai-command-agent',
        authentication: 'VM system-assigned Managed Identity',
        secretsRequired: false,
        stage: '2A + 2B.3 + 2C',
        operatingMode: 'context analysis + autonomous read-only investigation + three-tier routing',
        toolsEnabled: 0,
        reachable: false,
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setStatusLoading(false);
    }
  }, [customerId]);

  const loadHistory = useCallback(async () => {
    if (!customerId || !canCapability('ai_command_control', 'view_mission_history')) { setHistory([]); return; }
    setHistoryLoading(true);
    try {
      const data = await apiFetch<{ missions?: AiC2MissionSummary[] }>(`/ai-command-control/missions?customerId=${encodeURIComponent(customerId)}`);
      setHistory(Array.isArray(data.missions) ? data.missions : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [customerId, canCapability]);

  const loadWorkspace = useCallback(async (missionId: string) => {
    if (!customerId || !canCapability('ai_command_control', 'view_investigation_workspace')) {
      setWorkspace(null);
      return;
    }
    setWorkspaceLoading(true);
    setWorkspaceError('');
    try {
      const data = await apiFetch<{ workspace?: AiC2Workspace }>(`/ai-command-control/missions/${encodeURIComponent(missionId)}/workspace?customerId=${encodeURIComponent(customerId)}`);
      setWorkspace(data.workspace ?? null);
    } catch (error) {
      setWorkspace(null);
      setWorkspaceError(error instanceof Error ? error.message : String(error));
    } finally {
      setWorkspaceLoading(false);
    }
  }, [customerId, canCapability]);

  const loadMission = useCallback(async (missionId: string) => {
    if (!customerId) return;
    setDetailLoading(true);
    try {
      const data = await apiFetch<{ mission: AiC2Mission }>(`/ai-command-control/missions/${encodeURIComponent(missionId)}?customerId=${encodeURIComponent(customerId)}`);
      setSelectedMission(data.mission);
      setMissionMode(data.mission.missionMode ?? 'context');
      setOperatingLevel(data.mission.operatingLevel ?? 'l1');
      setReasoningDepth(data.mission.reasoningDepth ?? 'standard');
      setStream((prev) => prev.missionId === missionId && prev.status !== 'streaming' ? EMPTY_STREAM : prev);
    } finally {
      setDetailLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    setSelectedMission(null);
    setWorkspace(null);
    setWorkspaceError('');
    setStream(EMPTY_STREAM);
    void loadStatus(false);
    void loadHistory();
  }, [customerId, loadStatus, loadHistory]);


  useEffect(() => {
    const missionId = selectedMission?.id;
    if (!missionId || status?.stage2CEnabled === false) {
      setWorkspace(null);
      return;
    }
    void loadWorkspace(missionId);
  }, [selectedMission?.id, status?.stage2CEnabled, loadWorkspace]);

  useEffect(() => {
    if (stream.status === 'streaming') outputEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [stream.output, stream.status]);

  useEffect(() => () => streamAbortRef.current?.abort(), []);

  const processEvent = useCallback((eventName: string, data: Record<string, unknown>) => {
    if (eventName === 'mission') {
      const missionId = typeof data['missionId'] === 'string' ? data['missionId'] : null;
      if (missionId) liveMissionIdRef.current = missionId;
      setStream((prev) => ({ ...prev, missionId: missionId ?? prev.missionId, status: 'streaming' }));
      return;
    }
    if (eventName === 'delta') {
      const text = typeof data['text'] === 'string' ? data['text'] : '';
      if (text) setStream((prev) => ({ ...prev, output: prev.output + text }));
      return;
    }
    if (eventName === 'progress') {
      const percent = Math.max(0, Math.min(100, Number(data['percent'] ?? 0) || 0));
      const phase = String(data['phase'] ?? 'Mission progress');
      const detail = String(data['detail'] ?? '');
      setStream((prev) => ({ ...prev, progress: { percent: Math.max(prev.progress.percent, percent), phase, detail } }));
      return;
    }
    if (eventName === 'activity' || eventName === 'evidence') {
      const label = String(data['label'] ?? (eventName === 'evidence' ? 'Evidence attached' : 'Agent activity'));
      setStream((prev) => ({ ...prev, activity: [...prev.activity, { eventType: eventName, label, detail: typeof data['detail'] === 'string' ? data['detail'] : null, metadata: data, createdAt: new Date().toISOString() }] }));
      return;
    }
    if (eventName === 'complete') {
      const metrics = (data['metrics'] as AiC2Metrics | undefined);
      const disposition = String(data['disposition'] ?? metrics?.disposition ?? 'success').replace(/_/g, ' ');
      const missionId = String(data['missionId'] ?? liveMissionIdRef.current ?? 'mission');
      setStream((prev) => ({ ...prev, status: 'completed', metrics: metrics ?? prev.metrics, progress: { percent: 100, phase: 'Mission complete', detail: `Completed · ${disposition}` } }));
      if (completionToastRef.current !== missionId) {
        completionToastRef.current = missionId;
        toast({ title: 'AI C2 mission completed', description: `${disposition} · ${metrics?.toolCalls ?? 0} read-only tool call(s) · final tier ${metrics?.finalTier ?? 'n/a'}` });
      }
      return;
    }
    if (eventName === 'cancelled') {
      setStream((prev) => ({ ...prev, status: 'cancelled' }));
      return;
    }
    if (eventName === 'error') {
      setStream((prev) => ({ ...prev, status: 'failed', error: String(data['message'] ?? data['error'] ?? 'AI C2 mission failed') }));
    }
  }, []);

  const runStream = useCallback(async (path: string, body: Record<string, unknown>, resetOutput: boolean) => {
    liveMissionIdRef.current = resetOutput ? null : (selectedMission?.id ?? liveMissionIdRef.current);
    if (streamAbortRef.current) streamAbortRef.current.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;
    if (resetOutput) setStream({ ...EMPTY_STREAM, status: 'streaming' });
    else setStream((prev) => ({ ...prev, output: '', status: 'streaming', error: null, metrics: null, progress: { percent: 8, phase: 'Follow-up accepted', detail: 'Merging analyst follow-up into the persisted mission state.' }, activity: [...prev.activity, { eventType: 'activity', label: 'Follow-up submitted', createdAt: new Date().toISOString() }] }));
    try {
      const response = await fetch(`${getApiBase()}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      await parseSseResponse(response, processEvent);
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') {
        setStream((prev) => ({ ...prev, status: 'failed', error: error instanceof Error ? error.message : String(error) }));
      }
    } finally {
      streamAbortRef.current = null;
      setTimeout(() => {
        void loadHistory();
        const id = liveMissionIdRef.current;
        if (id) void loadMission(id);
      }, 200);
    }
  }, [loadHistory, loadMission, processEvent, selectedMission?.id]);

  const launchMission = useCallback(async () => {
    if (!customerId || !prompt.trim() || !canLevel(operatingLevel) || !canReasoning(reasoningDepth) || (missionMode === 'autonomous' && !canAutonomous)) return;
    setSelectedMission(null);
    await runStream('/ai-command-control/missions/stream', {
      customerId,
      prompt: prompt.trim(),
      operatingLevel,
      reasoningDepth,
      missionMode,
    }, true);
  }, [customerId, prompt, operatingLevel, reasoningDepth, missionMode, canAutonomous, canLevel, canReasoning, runStream]);

  const continueMission = useCallback(async () => {
    const missionId = selectedMission?.id ?? stream.missionId;
    if (!missionId || !customerId || !followUp.trim()) return;
    await runStream(`/ai-command-control/missions/${encodeURIComponent(missionId)}/continue/stream`, {
      customerId,
      prompt: followUp.trim(),
    }, false);
    setFollowUp('');
  }, [selectedMission?.id, stream.missionId, customerId, followUp, runStream]);

  const cancelMission = useCallback(async () => {
    const missionId = stream.missionId ?? selectedMission?.id;
    if (!missionId || !customerId) return;
    try {
      await apiFetch(`/ai-command-control/missions/${encodeURIComponent(missionId)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      setStream((prev) => ({ ...prev, status: 'cancelled' }));
    } finally {
      setTimeout(() => { void loadHistory(); void loadMission(missionId); }, 250);
    }
  }, [stream.missionId, selectedMission?.id, customerId, loadHistory, loadMission]);

  const downloadMissionExport = useCallback(async (format: 'md' | 'txt', scope: 'workspace' | 'conversation' = 'workspace') => {
    const missionId = selectedMission?.id ?? stream.missionId;
    if (!missionId || !customerId || !canCapability('ai_command_control', 'export_mission')) return;
    try {
      const response = await fetch(`${getApiBase()}/ai-command-control/missions/${encodeURIComponent(missionId)}/export?customerId=${encodeURIComponent(customerId)}&format=${format}&scope=${scope}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `Export failed with HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') ?? '';
      const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? `KloudSecure-${workspace?.caseRef ?? missionId}-${scope === 'conversation' ? 'AI-C2-Conversation' : 'Investigation'}.${format}`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast({ title: scope === 'conversation' ? 'Conversation exported' : 'Investigation exported', description: `${filename} downloaded successfully.` });
    } catch (error) {
      toast({ title: 'Export failed', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    }
  }, [selectedMission?.id, stream.missionId, customerId, canCapability, workspace?.caseRef]);

  const openFobOverlay = useCallback(() => {
    if (!workspace || !customerId || !canCapability('ai_command_control', 'view_fob_threat_overlay')) return;
    try {
      sessionStorage.setItem('kloudsecure:ai-c2-fob-overlay', JSON.stringify({
        version: 1,
        missionId: workspace.missionId,
        caseRef: workspace.caseRef,
        customerId,
        snapshotId: workspace.fobOverlay.snapshotId ?? null,
        createdAt: new Date().toISOString(),
        entities: workspace.affectedEntities.map((entity) => ({ type: entity.type, value: entity.value, status: entity.status })),
        matchedNodeKeys: workspace.fobOverlay.nodes.map((node) => node.matchedFobNodeKey).filter(Boolean),
      }));
      if (onNavigate) onNavigate('forward_operating_base', customerId);
      else toast({ title: 'FOB overlay prepared', description: 'Open Forward Operating Base to view the AI C2 threat overlay.' });
    } catch (error) {
      toast({ title: 'Unable to prepare FOB overlay', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    }
  }, [workspace, customerId, canCapability, onNavigate]);


  const openMobOverlay = useCallback(() => {
    if (!workspace || !customerId || !canCapability('ai_command_control', 'view_mob_architecture_overlay') || !canCapability('main_operating_base', 'receive_ai_c2_overlay')) return;
    try {
      sessionStorage.setItem('kloudsecure:ai-c2-mob-overlay', JSON.stringify({
        version: 1,
        missionId: workspace.missionId,
        caseRef: workspace.caseRef,
        customerId,
        sourceFobSnapshotId: workspace.fobOverlay.snapshotId ?? null,
        createdAt: new Date().toISOString(),
        entities: workspace.affectedEntities.map((entity) => ({ type: entity.type, value: entity.value, status: entity.status })),
        timeline: workspace.timeline.map((item) => ({ time: item.time, sortKey: item.sortKey, text: item.text, category: item.category })),
      }));
      if (onNavigate) onNavigate('main_operating_base', customerId);
      else toast({ title: 'MoB projection prepared', description: 'Open Main Operating Base to project the AI C2 case into the live security architecture.' });
    } catch (error) {
      toast({ title: 'Unable to prepare MoB projection', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    }
  }, [workspace, customerId, canCapability, onNavigate]);

  const visibleMessages = selectedMission?.messages ?? [];
  const currentMetrics = stream.metrics ?? selectedMission?.metrics ?? null;
  const currentEvidence = canCapability('ai_command_control', 'view_evidence') ? (selectedMission?.evidence ?? []) : [];
  const currentEvents = canCapability('ai_command_control', 'view_agent_activity') ? (selectedMission?.events ?? stream.activity) : [];
  const currentToolCalls = canCapability('ai_command_control', 'view_tool_activity') ? (selectedMission?.toolCalls ?? []) : [];
  const persistedProgress = useMemo<AiC2ProgressState>(() => {
    const progressEvents = (selectedMission?.events ?? []).filter((event) => event.eventType === 'progress');
    const last = progressEvents[progressEvents.length - 1];
    const metadata = last?.metadata ?? {};
    return { percent: Math.max(0, Math.min(100, Number(metadata['percent'] ?? (selectedMission?.status === 'completed' ? 100 : 0)) || 0)), phase: String(metadata['phase'] ?? (selectedMission?.status === 'completed' ? 'Mission complete' : 'Standing by')), detail: String(metadata['detail'] ?? last?.detail ?? '') };
  }, [selectedMission]);
  const showingLiveProgress = stream.status !== 'idle' && (!selectedMission || stream.missionId === selectedMission.id);
  const currentProgress = showingLiveProgress ? stream.progress : persistedProgress;
  const canContinue = canCapability('ai_command_control', 'continue_thread') && Boolean(selectedMission?.foundryResponseId || selectedMission?.missionMode === 'autonomous' || (stream.missionId && stream.status === 'completed'));

  return (
    <div className="space-y-4 pb-10">
      <section className="overflow-hidden rounded-2xl border border-cyan-500/25 bg-card shadow-[0_0_40px_rgba(6,182,212,0.06)]">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border bg-gradient-to-r from-slate-950 via-slate-950 to-cyan-950 px-5 py-4">
          <div>
            <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">
              <BrainCircuit className="h-4 w-4" /> AI Command &amp; Control Center · Stages 2A + 2B.3 + 2C
            </div>
            <h2 className="text-xl font-bold text-white">Mission Control</h2>
            <p className="mt-1 max-w-4xl text-xs text-slate-300">
              Foundry-powered SOC reasoning through the KloudSecure VM managed identity. Stage 2B.3 hardens autonomous read-only reasoning and evidence fidelity. Stage 2C turns every persisted mission into a human Investigation Workspace with case metrics, attack timeline, hypotheses, RCA, attack story, MITRE mapping, affected entities, blast radius, evidence gaps, confidence, recommended investigative actions, export, and an optional permission-gated Forward Operating Base threat overlay. Sol remains reserved for genuinely ambiguous/high-impact cases; the entire investigation toolbelt remains read-only.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold">
            <span className="rounded-full border border-emerald-500/35 bg-emerald-500/10 px-3 py-1.5 text-emerald-300">Managed Identity</span>
            <span className="rounded-full border border-cyan-500/35 bg-cyan-500/10 px-3 py-1.5 text-cyan-300">READ ONLY</span>
            <span className="rounded-full border border-violet-500/35 bg-violet-500/10 px-3 py-1.5 text-violet-300">{status?.toolsEnabled ?? 0} READ-ONLY TOOLS</span>
          </div>
        </div>

        <div className="grid gap-3 p-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Mandatory customer context</div>
            <select
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
              className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none focus:border-cyan-500"
            >
              <option value="">Select customer explicitly…</option>
              {realCustomers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
            </select>
            {selectedCustomer && (
              <div className="mt-3 grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-3">
                <div><span className="text-muted-foreground">Tenant</span><br /><span className="text-foreground">{selectedCustomer.tenantId ?? '-'}</span></div>
                <div><span className="text-muted-foreground">Workspace</span><br /><span className="text-foreground">{selectedCustomer.workspaceName ?? '-'}</span></div>
                <div><span className="text-muted-foreground">Subscription</span><br /><span className="text-foreground">{selectedCustomer.subscription ?? '-'}</span></div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Foundry runtime · AI C2 router</div>
                <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground"><Bot className="h-4 w-4 text-violet-300" /> Luna → Terra → Sol</div>
              </div>
              <button
                type="button"
                disabled={!customerId || statusLoading}
                onClick={() => void loadStatus(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-2 text-[11px] font-semibold text-muted-foreground hover:border-cyan-500/50 hover:text-cyan-700 dark:text-cyan-200 disabled:opacity-40"
              >
                {statusLoading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Radio className="h-3.5 w-3.5" />} Probe all
              </button>
            </div>
            <div className="mt-3 grid gap-2 text-[10px] sm:grid-cols-3">
              {(status?.agents ?? [
                { tier: 'worker' as const, label: 'Luna Worker', model: 'gpt-5.6-luna', deployment: 'kloudsecure-ai-command-worker-model', agentName: 'kloudsecure-ai-command-worker' },
                { tier: 'analyst' as const, label: 'Terra Analyst', model: 'gpt-5.6-terra', deployment: 'kloudsecure-ai-command-analyst-model', agentName: 'kloudsecure-ai-command-analyst' },
                { tier: 'expert' as const, label: 'Sol Expert', model: 'gpt-5.6-sol', deployment: 'kloudsecure-ai-command-reasoner', agentName: 'kloudsecure-ai-command-agent' },
              ]).map((agent) => {
                const probe = status?.probes?.find((item) => item.tier === agent.tier);
                return (
                  <div key={agent.tier} className="rounded-lg border border-border bg-card p-2">
                    <div className="flex items-center justify-between gap-2"><span className="font-semibold text-foreground">{agent.label}</span><span className={`text-[8px] font-bold uppercase ${probe?.ok === false ? 'text-rose-300' : probe?.ok ? 'text-emerald-300' : 'text-muted-foreground'}`}>{probe?.ok === false ? 'FAIL' : probe?.ok ? 'PASS' : agent.tier}</span></div>
                    <div className="mt-1 text-[9px] text-muted-foreground">{agent.model}</div>
                    <div className="truncate text-[8px] text-slate-700" title={agent.agentName}>{agent.agentName}</div>
                    {probe?.latencyMs != null && <div className="mt-1 text-[8px] text-muted-foreground">{probe.latencyMs} ms{probe.retries ? ` · ${probe.retries} retry` : ''}</div>}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
              <div className="rounded-lg border border-border bg-card p-2"><span className="text-muted-foreground">Authentication</span><div className="mt-1 text-foreground">{status?.authentication ?? 'VM Managed Identity'}</div></div>
              <div className="rounded-lg border border-border bg-card p-2"><span className="text-muted-foreground">Connectivity</span><div className={`mt-1 ${status?.reachable === false ? 'text-rose-300' : status?.reachable ? 'text-emerald-300' : 'text-muted-foreground'}`}>{status?.reachable === false ? 'One or more tiers failed' : status?.reachable ? 'ALL TIERS PASS' : 'Not probed'}</div></div>
              <div className="col-span-2 rounded-lg border border-violet-500/15 bg-violet-500/5 p-2"><span className="text-muted-foreground">Routing / resilience</span><div className="mt-1 text-violet-700 dark:text-violet-700 dark:text-violet-200/80">Luna evidence work → Terra correlation → Sol only for expert-grade ambiguity/high impact. 429 responses preserve mission state and retry automatically.</div></div>
            </div>
            {status?.message && <div className="mt-2 text-[10px] text-rose-300">{status.message}</div>}
          </div>
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[330px_minmax(0,1fr)_360px]">
        <aside className="space-y-4">
          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><TerminalSquare className="h-4 w-4 text-cyan-300" /> Mission parameters</div>
            <div className="space-y-3">
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Mission execution mode</div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button type="button" onClick={() => setMissionMode('context')} className={`rounded-lg border p-2 text-left ${missionMode === 'context' ? 'border-cyan-400 bg-cyan-50 dark:bg-cyan-500/12 text-cyan-900 dark:text-cyan-100' : 'border-border bg-card text-muted-foreground'}`}>
                    <div className="text-[11px] font-bold">Context Analysis</div><div className="mt-0.5 text-[9px] opacity-70">Stage 2A · supplied evidence only</div>
                  </button>
                  <button type="button" disabled={!canAutonomous} onClick={() => setMissionMode('autonomous')} className={`rounded-lg border p-2 text-left ${missionMode === 'autonomous' ? 'border-violet-400 bg-violet-50 dark:bg-violet-500/12 text-violet-900 dark:text-violet-100' : 'border-border bg-card text-muted-foreground'} disabled:cursor-not-allowed disabled:opacity-35`}>
                    <div className="text-[11px] font-bold">Autonomous Read-only</div><div className="mt-0.5 text-[9px] opacity-70">Stage 2B.3 · orchestrated investigation</div>
                  </button>
                </div>
              </div>
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">SOC operating level</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {LEVELS.map((level) => {
                    const enabled = canLevel(level.id);
                    const active = operatingLevel === level.id;
                    return (
                      <button key={level.id} type="button" disabled={!enabled} onClick={() => setOperatingLevel(level.id)} className={`rounded-lg border p-2 text-left transition ${active ? 'border-cyan-400 bg-cyan-50 dark:bg-cyan-500/12 text-cyan-900 dark:text-cyan-100' : 'border-border bg-card text-muted-foreground hover:border-slate-600'} disabled:cursor-not-allowed disabled:opacity-35`}>
                        <div className="text-[11px] font-bold">{level.label}</div>
                        <div className="mt-0.5 text-[9px] opacity-70">{level.caption}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Analysis depth profile</div>
                <div className="space-y-1.5">
                  {REASONING.map((option) => {
                    const enabled = canReasoning(option.id);
                    const active = reasoningDepth === option.id;
                    return (
                      <button key={option.id} type="button" disabled={!enabled} onClick={() => setReasoningDepth(option.id)} className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left ${active ? 'border-violet-400 bg-violet-50 dark:bg-violet-500/10 text-violet-900 dark:text-violet-100' : 'border-border bg-card text-muted-foreground'} disabled:cursor-not-allowed disabled:opacity-35`}>
                        <span><span className="text-[11px] font-semibold">{option.label}</span><span className="ml-2 text-[9px] opacity-60">{option.caption}</span></span>
                        <span className="rounded border border-current/20 px-1.5 py-0.5 text-[9px] uppercase">{option.profile}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 rounded-lg border border-violet-300 bg-violet-50 dark:border-violet-500/20 dark:bg-violet-500/5 p-2 text-[9px] leading-relaxed text-violet-800 dark:text-violet-200/75">
                  Foundry Prompt Agent owns the model's low-level reasoning configuration. These profiles guide investigation depth through the mission prompt; KloudSecure does not send a per-request reasoning override to an agent endpoint.
                </div>
              </div>
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/5 p-2 text-[10px] text-amber-800 dark:text-amber-200/80">
                Testing mode: no analyst-configured mission, token, time or tool-call budgets are applied. Stage 2B.3 uses progress-aware Luna → Terra routing, schema-aware KQL repair and graceful evidence-gap completion. All tools remain read-only and are executed by KloudSecure under customer/RBAC/KQL guardrails; no containment tools exist.
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground"><History className="h-4 w-4 text-muted-foreground" /> Mission history</div>
              <button type="button" disabled={!customerId || historyLoading} onClick={() => void loadHistory()} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"><RefreshCw className={`h-3.5 w-3.5 ${historyLoading ? 'animate-spin' : ''}`} /></button>
            </div>
            <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
              {!customerId && <div className="py-8 text-center text-xs text-muted-foreground">Select a customer to load mission history.</div>}
              {customerId && !historyLoading && history.length === 0 && <div className="py-8 text-center text-xs text-muted-foreground">No AI C2 missions for this customer yet.</div>}
              {history.map((mission) => (
                <button key={mission.id} type="button" onClick={() => void loadMission(mission.id)} className={`w-full rounded-xl border p-3 text-left transition ${selectedMission?.id === mission.id ? 'border-cyan-500/50 bg-cyan-500/7' : 'border-border bg-card hover:border-border'}`}>
                  <div className="line-clamp-2 text-[11px] font-semibold text-foreground">{mission.title}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase ${statusClass(mission.status)}`}>{mission.status}</span>
                    <span className="rounded-full border border-border px-1.5 py-0.5 text-[9px] uppercase text-muted-foreground">{mission.operatingLevel}</span>
                    <span className="rounded-full border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground">{mission.reasoningDepth}</span>
                    <span className={`rounded-full border px-1.5 py-0.5 text-[9px] ${mission.missionMode === 'autonomous' ? 'border-violet-500/30 text-violet-300' : 'border-cyan-500/25 text-cyan-300'}`}>{mission.missionMode === 'autonomous' ? 'Stage 2B.3' : 'Stage 2A'}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-[9px] text-muted-foreground"><span>{formatDate(mission.createdAt)} · {mission.createdByEmail ?? 'unknown analyst'}</span>{mission.metrics?.estimatedCostUsd != null && <span className="text-emerald-500/80">~${mission.metrics.estimatedCostUsd.toFixed(4)}</span>}</div>
                </button>
              ))}
            </div>
          </section>
        </aside>

        <main className="min-w-0 space-y-4">
          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground"><MessageSquareText className="h-4 w-4 text-cyan-300" /> Mission / detailed prompt</div>
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Detailed prompts encouraged</span>
            </div>
            <textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="Example: Review the supplied Forward Operating Base context for evidence that warrants escalation. Challenge the apparent alert classification, identify likely attack paths, separate confirmed facts from hypotheses, and specify the next telemetry pivots an L2/L3 analyst should execute…"
              className="min-h-[180px] w-full resize-y rounded-xl border border-border bg-card p-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-cyan-500"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-[10px] text-muted-foreground">{missionMode === 'autonomous' ? `Stage 2B.3 toolbelt: ${status?.toolsEnabled ?? 0} read-only tool(s) available` : `FOB context injection: ${canCapability('ai_command_control', 'use_fob_context') ? 'enabled when a snapshot is available and FOB read is permitted' : 'not permitted for this role'}`}</div>
              <div className="flex gap-2">
                {stream.status === 'streaming' && (
                  <button type="button" onClick={() => void cancelMission()} className="inline-flex items-center gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-200 hover:bg-rose-500/15">
                    <CircleStop className="h-4 w-4" /> Cancel mission
                  </button>
                )}
                <button
                  type="button"
                  disabled={!customerId || !prompt.trim() || stream.status === 'streaming' || !canLevel(operatingLevel) || !canReasoning(reasoningDepth) || (missionMode === 'autonomous' && !canAutonomous)}
                  onClick={() => void launchMission()}
                  className="inline-flex items-center gap-2 rounded-lg border border-cyan-400/50 bg-cyan-50 dark:bg-cyan-500/15 px-4 py-2 text-xs font-bold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {stream.status === 'streaming' ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Launch mission
                </button>
              </div>
            </div>
          </section>

          {canCapability('ai_command_control', 'view_investigation_workspace') && (
            <section className="overflow-hidden rounded-2xl border border-violet-500/25 bg-card shadow-[0_0_35px_rgba(139,92,246,0.06)]">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-slate-950 via-violet-950 to-slate-950 px-4 py-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold text-white"><GitBranch className="h-4 w-4 text-violet-300" /> Stage 2C · Investigation Workspace</div>
                  <div className="mt-1 text-[10px] text-slate-400">Deterministic case view generated from the persisted AI C2 mission, evidence ledger, tool activity and final adjudication.</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {workspace?.caseRef && <span className="rounded border border-violet-500/30 bg-violet-500/8 px-2 py-1 text-[9px] font-bold text-violet-700 dark:text-violet-200">{workspace.caseRef}</span>}
                  {workspace && <span className={`rounded border px-2 py-1 text-[9px] font-bold ${workspaceStateClass(workspace.state)}`}>{workspace.state}</span>}
                  {canCapability('ai_command_control', 'export_mission') && selectedMission && status?.stage2CExportsEnabled !== false && (
                    <>
                      <button type="button" onClick={() => void downloadMissionExport('md')} className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/8 px-2.5 py-1.5 text-[10px] font-bold text-cyan-700 dark:text-cyan-200 hover:bg-cyan-500/15" title="Download Investigation Workspace and original adjudication as Markdown">
                        <Download className="h-3.5 w-3.5" /> .MD
                      </button>
                      <button type="button" onClick={() => void downloadMissionExport('txt')} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 bg-muted px-2.5 py-1.5 text-[10px] font-bold text-foreground hover:bg-slate-700/70" title="Download Investigation Workspace and original adjudication as plain text">
                        <FileText className="h-3.5 w-3.5" /> .TXT
                      </button>
                    </>
                  )}
                </div>
              </div>

              {!selectedMission && !workspaceLoading && (
                <div className="flex min-h-[170px] flex-col items-center justify-center px-6 py-8 text-center">
                  <ListChecks className="mb-3 h-8 w-8 text-slate-700" />
                  <div className="text-sm font-semibold text-muted-foreground">Select a persisted mission to open its case workspace</div>
                  <div className="mt-1 max-w-xl text-xs text-muted-foreground">Completed Stage 2B.3 missions become operational cases here. Running missions can also be reopened to inspect the current persisted state.</div>
                </div>
              )}

              {workspaceLoading && (
                <div className="flex min-h-[170px] items-center justify-center gap-2 text-xs text-violet-700 dark:text-violet-200"><LoaderCircle className="h-4 w-4 animate-spin" /> Building Investigation Workspace from persisted evidence…</div>
              )}

              {workspaceError && !workspaceLoading && (
                <div className="m-4 rounded-xl border border-rose-500/30 bg-rose-500/8 p-3 text-xs text-rose-200"><XCircle className="mr-2 inline h-4 w-4" />{workspaceError}</div>
              )}

              {workspace && !workspaceLoading && (
                <div className="space-y-4 p-4">
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
                    {[
                      ['State', workspace.state],
                      ['Elapsed', formatElapsed(workspace.elapsedMs)],
                      ['Queries', workspace.counts.queries],
                      ['Evidence objects', workspace.counts.evidenceObjects],
                      ['Entities', workspace.counts.entities],
                      ['Hypotheses', workspace.counts.hypotheses],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="rounded-xl border border-border bg-card p-3">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
                        <div className="mt-1 text-sm font-black text-foreground">{value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-200"><Clock3 className="h-4 w-4" /> Attack timeline</div>
                        <span className="text-[9px] text-muted-foreground">{workspace.timeline.length} event(s)</span>
                      </div>
                      <div className="max-h-[430px] space-y-3 overflow-y-auto pr-1">
                        {workspace.timeline.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">No timestamped narrative events were extracted from the final adjudication.</div>}
                        {workspace.timeline.map((item, index) => (
                          <div key={`${item.time}-${index}`} className="grid grid-cols-[78px_1fr] gap-3">
                            <div className="pt-0.5 text-right font-mono text-[10px] font-bold text-cyan-300">{item.time}</div>
                            <div className="relative border-l border-border pl-3">
                              <span className={`absolute -left-1 top-1 h-2 w-2 rounded-full ${item.category === 'compromise' ? 'bg-rose-400' : item.category === 'identity' ? 'bg-amber-400' : item.category === 'network' ? 'bg-violet-400' : 'bg-cyan-400'}`} />
                              <div className="text-[10px] leading-4 text-muted-foreground">{item.text}</div>
                              <div className="mt-1 text-[8px] font-bold uppercase tracking-wider text-slate-700">{item.category}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-200"><Target className="h-4 w-4" /> Hypotheses</div>
                        <span className="text-[9px] text-muted-foreground">{workspace.hypotheses.length} active</span>
                      </div>
                      <div className="max-h-[430px] space-y-2 overflow-y-auto pr-1">
                        {workspace.hypotheses.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">No structured active hypothesis section was extracted.</div>}
                        {workspace.hypotheses.map((hypothesis) => (
                          <details key={hypothesis.id} className="rounded-lg border border-border bg-card p-3" open={hypothesis.status === 'confirmed' || hypothesis.status === 'likely'}>
                            <summary className="cursor-pointer list-none">
                              <div className="flex items-start justify-between gap-2">
                                <div><div className="text-[11px] font-bold text-foreground">{hypothesis.id} · {hypothesis.title}</div><div className="mt-1 text-[9px] text-muted-foreground">{hypothesis.assessment}</div></div>
                                <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[8px] font-bold uppercase ${hypothesisClass(hypothesis.status)}`}>{hypothesis.status}</span>
                              </div>
                            </summary>
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              <div><div className="mb-1 text-[8px] font-bold uppercase tracking-wider text-emerald-400">Supporting evidence</div>{hypothesis.supportingEvidence.length ? <ul className="space-y-1 text-[9px] leading-4 text-muted-foreground">{hypothesis.supportingEvidence.map((line, index) => <li key={index}>• {line}</li>)}</ul> : <div className="text-[9px] text-muted-foreground">None extracted.</div>}</div>
                              <div><div className="mb-1 text-[8px] font-bold uppercase tracking-wider text-amber-400">Limitations</div>{hypothesis.limitations.length ? <ul className="space-y-1 text-[9px] leading-4 text-muted-foreground">{hypothesis.limitations.map((line, index) => <li key={index}>• {line}</li>)}</ul> : <div className="text-[9px] text-muted-foreground">None extracted.</div>}</div>
                            </div>
                          </details>
                        ))}
                      </div>
                    </section>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-rose-200"><ShieldCheck className="h-4 w-4" /> Root Cause Analysis</div>
                      <div className="mb-2"><span className={`rounded border px-2 py-1 text-[8px] font-bold uppercase ${workspace.rca.status === 'resolved' ? 'border-emerald-500/30 text-emerald-300' : workspace.rca.status === 'partial' ? 'border-amber-500/30 text-amber-300' : 'border-violet-500/30 text-violet-300'}`}>{workspace.rca.status}</span></div>
                      <div className="text-[11px] leading-5 text-muted-foreground">{workspace.rca.conclusion}</div>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-200"><Activity className="h-4 w-4" /> Attack story</div>
                      <ol className="space-y-2">
                        {workspace.attackStory.slice(0, 14).map((line, index) => <li key={index} className="grid grid-cols-[24px_1fr] gap-2 text-[10px] leading-4 text-muted-foreground"><span className="flex h-5 w-5 items-center justify-center rounded-full border border-cyan-500/25 bg-cyan-500/8 text-[8px] font-bold text-cyan-300">{index + 1}</span><span>{line}</span></li>)}
                      </ol>
                    </section>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-3">
                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-orange-200"><Target className="h-4 w-4" /> MITRE ATT&amp;CK</div>
                      <div className="space-y-2">
                        {workspace.mitre.length === 0 && <div className="text-[10px] text-muted-foreground">No confirmed-behavior or explicit MITRE mappings extracted.</div>}
                        {workspace.mitre.map((item) => <div key={item.technique} className="rounded-lg border border-border bg-card p-2.5"><div className="text-[10px] font-bold text-orange-200">{item.technique} · {item.name}</div><div className="mt-1 text-[9px] leading-4 text-muted-foreground">{item.basis}</div></div>)}
                      </div>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-200"><Network className="h-4 w-4" /> Affected / investigative entities</div>
                      <div className="flex max-h-[320px] flex-wrap gap-2 overflow-y-auto">
                        {workspace.affectedEntities.map((entity) => <div key={entity.id} title={entity.context} className={`rounded-lg border px-2.5 py-2 ${entityClass(entity.status)}`}><div className="text-[8px] font-bold uppercase opacity-70">{entity.type} · {entity.status.replace(/_/g, ' ')}</div><div className="mt-0.5 max-w-[260px] truncate text-[10px] font-semibold">{entity.value}</div></div>)}
                      </div>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-200"><AlertTriangle className="h-4 w-4" /> Blast radius</div>
                      <div className="mb-2 text-[8px] font-bold uppercase tracking-wider text-amber-300">{workspace.blastRadius.status.replace(/_/g, ' ')}</div>
                      <div className="text-[10px] leading-5 text-muted-foreground">{workspace.blastRadius.summary}</div>
                    </section>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-3">
                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-amber-200">Evidence gaps</div>
                      <ul className="max-h-[320px] space-y-1.5 overflow-y-auto text-[9px] leading-4 text-muted-foreground">{workspace.evidenceGaps.map((gap, index) => <li key={index}>• {gap}</li>)}</ul>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-emerald-200">Confidence</div>
                      <div className="max-h-[320px] space-y-2 overflow-y-auto">{workspace.confidence.map((item, index) => <div key={index} className="rounded-lg border border-border bg-card p-2"><div className="text-[9px] font-semibold leading-4 text-muted-foreground">{item.assessment}</div><div className="mt-1 text-[9px] font-bold text-emerald-300">{item.confidence}</div></div>)}</div>
                    </section>

                    <section className="rounded-xl border border-border bg-card p-4">
                      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-200">Recommended investigative actions</div>
                      <div className="max-h-[320px] space-y-2 overflow-y-auto">{workspace.recommendedActions.map((action) => <details key={`${action.priority}-${action.title}`} className="rounded-lg border border-border bg-card p-2.5"><summary className="cursor-pointer text-[9px] font-bold text-cyan-700 dark:text-cyan-200">P{action.priority} · {action.title}</summary><ul className="mt-2 space-y-1 text-[9px] leading-4 text-muted-foreground">{action.steps.map((step, index) => <li key={index}>• {step}</li>)}</ul></details>)}</div>
                    </section>
                  </div>

                  {canViewCommander && (
                    <section className="overflow-hidden rounded-xl border border-violet-400/30 bg-gradient-to-br from-slate-950 via-violet-950 to-slate-950">
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-200"><ShieldCheck className="h-4 w-4" /> Stage 3C · Response Commander</div>
                          <div className="mt-1 max-w-3xl text-[9px] leading-4 text-muted-foreground">Completed case evidence → current Stage 3B.6-C contract → model proposal → server validation → analyst review → current-contract recheck → Stage 3B.5 live preflight → exact confirmation. Response Commander has no direct execution path.</div>
                        </div>
                        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/8 px-2 py-1 text-[8px] font-black uppercase tracking-wider text-emerald-300">Proposal only</span>
                      </div>
                      <div className="space-y-3 p-4">
                        <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                          <div>
                            <div className="mb-1 text-[8px] font-bold uppercase tracking-wider text-slate-400">Affected entity target</div>
                            <select value={commanderTarget?.key ?? ''} onChange={(event) => setCommanderTargetKey(event.target.value)} disabled={!commanderTargets.length || commanderLoading} className="w-full rounded-lg border border-border bg-card px-3 py-2 text-[10px] text-foreground outline-none focus:border-violet-500 disabled:opacity-40">
                              {commanderTargets.length === 0 && <option value="">No response-eligible case entity</option>}
                              {commanderTargets.map((item) => <option key={item.key} value={item.key}>{item.responseType.toUpperCase()} · {item.entity.value} · {item.entity.status.replaceAll('_',' ')}</option>)}
                            </select>
                          </div>
                          <div className="flex items-end">
                            <button type="button" onClick={() => void generateCommanderProposals()} disabled={!canGenerateCommander || !commanderTarget || commanderLoading || selectedMission?.status !== 'completed'} className="inline-flex min-h-[34px] items-center gap-1.5 rounded-lg border border-violet-400/40 bg-violet-500/12 px-3 py-2 text-[10px] font-black text-violet-900 dark:text-violet-100 hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-35">
                              {commanderLoading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate response proposals
                            </button>
                          </div>
                        </div>

                        {commanderError && <div className="rounded-lg border border-rose-500/25 bg-rose-500/8 p-2.5 text-[9px] text-rose-200">{commanderError}</div>}
                        {commanderDiagnostics.length > 0 && <details className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5"><summary className="cursor-pointer text-[9px] font-bold text-amber-200">Validation diagnostics · {commanderDiagnostics.length}</summary><ul className="mt-2 space-y-1 text-[8px] text-amber-100/60">{commanderDiagnostics.map((item,index)=><li key={index}>• {item}</li>)}</ul></details>}

                        {!commanderLoading && commanderTarget && commanderProposals.length === 0 && <div className="rounded-lg border border-border bg-card p-4 text-center text-[10px] text-muted-foreground">No persisted Response Commander proposals for this entity yet. Generation may also legitimately return zero actions when the evidence or current capability contract does not justify containment.</div>}

                        <div className="grid gap-3 xl:grid-cols-3">
                          {commanderProposals.slice(0, 12).map((proposal) => {
                            const guard = commanderGuards[proposal.id];
                            const guardApproved = Boolean(guard && (guard.guardStatus === 'READY_FOR_HANDOFF' || (guard.guardStatus === 'HUMAN_REVIEW_REQUIRED' && guard.reviewStatus === 'APPROVED')));
                            const guardExpired = Boolean(guard && new Date(guard.expiresAt).getTime() <= Date.now());
                            const canOpenHandoff = guardFeatureUnavailable || (guardApproved && !guardExpired);
                            const guardTone = guard?.guardStatus === 'BLOCKED' ? 'border-rose-500/30 bg-rose-500/[0.05]' : guard?.guardStatus === 'HUMAN_REVIEW_REQUIRED' ? 'border-amber-500/30 bg-amber-500/[0.05]' : guard?.guardStatus === 'READY_FOR_HANDOFF' ? 'border-emerald-500/30 bg-emerald-500/[0.05]' : 'border-border bg-card';
                            return (
                            <div key={proposal.id} className={`rounded-xl border p-3 ${proposal.status === 'ACCEPTED_FOR_PREFLIGHT' ? 'border-emerald-500/30 bg-emerald-500/[0.05]' : proposal.status === 'REJECTED' ? 'border-border bg-card opacity-65' : 'border-violet-500/25 bg-card'}`}>
                              <div className="flex flex-wrap items-start justify-between gap-2"><div><div className="text-[10px] font-black text-foreground">{proposal.actionLabel}</div><div className="mt-0.5 text-[8px] uppercase tracking-wider text-muted-foreground">{proposal.provider} · {proposal.riskClass} risk · {proposal.confidence} confidence</div></div><span className={`rounded-full border px-2 py-0.5 text-[7px] font-black uppercase ${proposal.status === 'ACCEPTED_FOR_PREFLIGHT' ? 'border-emerald-500/30 text-emerald-300' : proposal.status === 'REJECTED' ? 'border-border text-muted-foreground' : 'border-violet-500/30 text-violet-300'}`}>{proposal.status.replaceAll('_',' ')}</span></div>
                              <div className="mt-2 text-[9px] leading-4 text-muted-foreground">{proposal.rationale}</div>
                              <div className="mt-2 rounded-lg border border-border bg-card p-2"><div className="text-[7px] font-bold uppercase tracking-wider text-muted-foreground">Expected outcome</div><div className="mt-1 text-[8px] leading-4 text-muted-foreground">{proposal.expectedOutcome}</div></div>
                              {proposal.evidenceBasis.length > 0 && <div className="mt-2"><div className="text-[7px] font-bold uppercase tracking-wider text-muted-foreground">Evidence basis</div><ul className="mt-1 space-y-0.5 text-[8px] leading-4 text-muted-foreground">{proposal.evidenceBasis.slice(0,4).map((item,index)=><li key={index}>• {item}</li>)}</ul></div>}
                              {proposal.missingRequiredInputs.length > 0 && <div className="mt-2 rounded border border-amber-500/20 bg-amber-500/5 px-2 py-1.5 text-[8px] text-amber-300">Analyst completion required: {proposal.missingRequiredInputs.join(', ')}</div>}

                              {proposal.status === 'ACCEPTED_FOR_PREFLIGHT' && !guardFeatureUnavailable && (
                                <div className={`mt-3 rounded-lg border p-2.5 ${guardTone}`}>
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                      <div className="text-[8px] font-black uppercase tracking-wider text-sky-200">Stage 4 · Independent Judge + Policy Guard</div>
                                      <div className="mt-1 text-[8px] text-muted-foreground">Independent Sol review followed by deterministic customer policy. This layer cannot execute the response.</div>
                                    </div>
                                    {guard && <span className={`rounded-full border px-2 py-0.5 text-[7px] font-black uppercase ${guard.guardStatus === 'BLOCKED' ? 'border-rose-500/35 text-rose-300' : guard.guardStatus === 'HUMAN_REVIEW_REQUIRED' ? 'border-amber-500/35 text-amber-300' : 'border-emerald-500/35 text-emerald-300'}`}>{guard.guardStatus.replaceAll('_',' ')}</span>}
                                  </div>
                                  {!guard && <div className="mt-2 text-[8px] text-muted-foreground">Not evaluated yet. Run Stage 4 before Native Response handoff.</div>}
                                  {guard && <>
                                    <div className="mt-2 grid grid-cols-2 gap-1.5 text-[8px]">
                                      <div className="rounded border border-border bg-card p-1.5"><div className="text-[7px] uppercase text-muted-foreground">Judge</div><div className="mt-0.5 font-bold text-muted-foreground">{guard.judgeVerdict.replaceAll('_',' ')} · {guard.judgeConfidence}</div></div>
                                      <div className="rounded border border-border bg-card p-1.5"><div className="text-[7px] uppercase text-muted-foreground">Policy</div><div className="mt-0.5 font-bold text-muted-foreground">{guard.policyDecision.replaceAll('_',' ')}</div></div>
                                    </div>
                                    <div className="mt-2 text-[8px] leading-4 text-muted-foreground">{guard.judgeSummary}</div>
                                    {guard.reasons.length > 0 && <ul className="mt-1.5 space-y-0.5 text-[8px] leading-4 text-muted-foreground">{guard.reasons.slice(0,4).map((reason,index)=><li key={index}>• {reason}</li>)}</ul>}
                                    {guard.contradictoryEvidence.length > 0 && <details className="mt-2 rounded border border-amber-500/15 bg-amber-500/[0.03] px-2 py-1.5"><summary className="cursor-pointer text-[8px] font-bold text-amber-300">Contradictory evidence · {guard.contradictoryEvidence.length}</summary><ul className="mt-1 space-y-0.5 text-[8px] text-amber-100/60">{guard.contradictoryEvidence.slice(0,5).map((item,index)=><li key={index}>• {item}</li>)}</ul></details>}
                                    {guard.protectedTarget && <div className="mt-2 rounded border border-amber-500/25 bg-amber-500/5 px-2 py-1.5 text-[8px] text-amber-300">Protected target policy matched{guard.matchedProtectionPatterns.length ? `: ${guard.matchedProtectionPatterns.join(', ')}` : '.'}</div>}
                                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[7px] text-muted-foreground"><span>Evidence: {guard.evidenceSufficiency}</span><span>Proportionality: {guard.proportionality}</span><span>{guardExpired ? 'Expired' : `Valid until ${new Date(guard.expiresAt).toLocaleTimeString()}`}</span></div>
                                    {guard.guardStatus === 'HUMAN_REVIEW_REQUIRED' && <div className="mt-2 text-[8px] font-bold text-amber-300">Human guard: {guard.reviewStatus.replaceAll('_',' ')}</div>}
                                  </>}
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {canEvaluateGuard && <button type="button" onClick={() => void evaluateCommanderGuard(proposal)} disabled={guardBusy !== null} className="rounded border border-sky-500/30 bg-sky-500/8 px-2 py-1.5 text-[8px] font-bold text-sky-200 hover:bg-sky-500/15 disabled:opacity-35">{guardBusy === `evaluate:${proposal.id}` ? 'Evaluating…' : guard ? 'Re-run Judge & Guard' : 'Run Independent Judge & Guard'}</button>}
                                    {guard?.guardStatus === 'HUMAN_REVIEW_REQUIRED' && guard.reviewStatus === 'PENDING' && canReviewGuard && <><button type="button" onClick={() => void reviewCommanderGuard(guard,'approved')} disabled={guardBusy !== null} className="rounded border border-emerald-500/30 bg-emerald-500/8 px-2 py-1.5 text-[8px] font-bold text-emerald-200 hover:bg-emerald-500/15 disabled:opacity-35">Approve guard for preflight</button><button type="button" onClick={() => void reviewCommanderGuard(guard,'denied')} disabled={guardBusy !== null} className="rounded border border-rose-500/30 bg-rose-500/8 px-2 py-1.5 text-[8px] font-bold text-rose-200 hover:bg-rose-500/15 disabled:opacity-35">Deny</button></>}
                                  </div>
                                </div>
                              )}
                              {proposal.status === 'ACCEPTED_FOR_PREFLIGHT' && guardFeatureUnavailable && <div className="mt-3 rounded border border-border bg-card px-2 py-1.5 text-[8px] text-muted-foreground">Stage 4 feature is disabled. Existing Stage 3C contract revalidation and Stage 3B.5 live preflight remain mandatory.</div>}

                              <div className="mt-3 flex flex-wrap gap-1.5">
                                {proposal.status === 'PROPOSED' && canReviewCommander && <><button type="button" onClick={() => void reviewCommanderProposal(proposal,'accepted_for_preflight')} className="rounded border border-emerald-500/30 bg-emerald-500/8 px-2 py-1.5 text-[8px] font-bold text-emerald-200 hover:bg-emerald-500/15">Accept for preflight</button><button type="button" onClick={() => void reviewCommanderProposal(proposal,'rejected')} className="rounded border border-border bg-card px-2 py-1.5 text-[8px] font-bold text-muted-foreground hover:text-rose-300">Reject</button></>}
                                {proposal.status === 'ACCEPTED_FOR_PREFLIGHT' && canHandoffCommander && canOpenHandoff && <button type="button" onClick={() => void openCommanderHandoff(proposal)} className="rounded border border-violet-400/35 bg-violet-500/10 px-2 py-1.5 text-[8px] font-bold text-violet-700 dark:text-violet-200 hover:bg-violet-500/18">Prepare Native Response</button>}
                                {proposal.status === 'ACCEPTED_FOR_PREFLIGHT' && !guardFeatureUnavailable && !canOpenHandoff && <span className="rounded border border-border bg-card px-2 py-1.5 text-[8px] text-muted-foreground">Handoff locked by Stage 4 guard</span>}
                              </div>
                              <div className="mt-2 truncate font-mono text-[7px] text-slate-700" title={proposal.id}>{proposal.id}</div>
                            </div>
                            );
                          })}
                        </div>

                        {commanderHandoff && <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/[0.04] p-3">
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><div><div className="text-[9px] font-black uppercase tracking-wider text-cyan-700 dark:text-cyan-200">Reviewed proposal → Native Response</div><div className="mt-1 text-[8px] text-muted-foreground">Current contract {commanderHandoff.handoff.currentContractVersion} / resolver {commanderHandoff.handoff.currentResolverVersion} was rechecked before this handoff. Nothing has been preflighted or executed yet.</div></div><button type="button" onClick={() => setCommanderHandoff(null)} className="text-[8px] text-muted-foreground hover:text-foreground">Close</button></div>
                          <NativeResponseActions customerId={customerId} entityType={commanderHandoff.handoff.entityType} entityValue={commanderHandoff.handoff.entityValue} source="aic2" mobSnapshotId={commanderHandoff.handoff.mobSnapshotId} mobNodeKey={commanderHandoff.handoff.mobNodeKey} compact commanderSuggestion={{ proposalId: commanderHandoff.proposalId, actionId: commanderHandoff.handoff.actionId, parameters: commanderHandoff.handoff.parameters as any, justification: commanderHandoff.handoff.justification, missingRequiredInputs: commanderHandoff.handoff.missingRequiredInputs }} />
                        </div>}
                      </div>
                    </section>
                  )}

                  {canCapability('ai_command_control', 'view_fob_threat_overlay') && (
                    <section className="overflow-hidden rounded-xl border border-fuchsia-500/25 bg-gradient-to-br from-slate-950 via-fuchsia-950 to-slate-950">
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-200"><Network className="h-4 w-4" /> FOB Active Threat Overlay</div>
                          <div className="mt-1 text-[9px] text-slate-400">{workspace.fobOverlay.matchedNodeCount} matched terrain node(s) · {workspace.fobOverlay.missionEntityCount} mission entities · snapshot {workspace.fobOverlay.snapshotId ?? 'not linked'}</div>
                        </div>
                        <button type="button" disabled={!workspace.fobOverlay.available || workspace.fobOverlay.matchedNodeCount === 0} onClick={openFobOverlay} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-500/35 bg-fuchsia-500/10 px-3 py-2 text-[10px] font-bold text-fuchsia-700 dark:text-fuchsia-200 hover:bg-fuchsia-500/15 disabled:cursor-not-allowed disabled:opacity-40">
                          <ExternalLink className="h-3.5 w-3.5" /> Illuminate in FOB
                        </button>
                      </div>
                      <div className="p-4">
                        {workspace.fobOverlay.nodes.length === 0 ? <div className="py-5 text-center text-[10px] text-slate-400">No mission entities could be mapped into the linked FOB snapshot.</div> : (
                          <div className="flex flex-wrap items-center gap-2">
                            {workspace.fobOverlay.nodes.slice(0, 24).map((node, index) => (
                              <div key={node.key} className={`relative rounded-xl border px-3 py-2 ${entityClass(node.status)}`}>
                                <div className="text-[8px] font-bold uppercase opacity-70">{node.type} · {node.matchedFobNodeKey ? 'FOB matched' : 'mission only'}</div>
                                <div className="mt-0.5 max-w-[220px] truncate text-[10px] font-bold">{node.label}</div>
                                {index < workspace.fobOverlay.nodes.length - 1 && <span className="absolute -right-2.5 top-1/2 hidden h-px w-2.5 bg-slate-700 xl:block" />}
                              </div>
                            ))}
                          </div>
                        )}
                        {workspace.fobOverlay.edges.length > 0 && <div className="mt-3 text-[9px] text-muted-foreground">{workspace.fobOverlay.edges.length} existing FOB evidence relationship(s) connect matched mission entities. Stage 2C does not invent graph edges.</div>}
                      </div>
                    </section>
                  )}


                  {canCapability('ai_command_control', 'view_mob_architecture_overlay') && canCapability('main_operating_base', 'receive_ai_c2_overlay') && (
                    <section className="overflow-hidden rounded-xl border border-cyan-400/25 bg-gradient-to-br from-slate-950 via-cyan-950 to-violet-950">
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cyan-700 dark:text-cyan-200"><Network className="h-4 w-4" /> MoB Live Architecture Projection</div>
                          <div className="mt-1 text-[9px] text-slate-400">Project this case into Main Operating Base without creating synthetic architecture relationships.</div>
                        </div>
                        <button type="button" disabled={workspace.affectedEntities.length === 0} onClick={openMobOverlay} className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/35 bg-cyan-400/10 px-3 py-2 text-[10px] font-bold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-400/15 disabled:cursor-not-allowed disabled:opacity-40">
                          <ExternalLink className="h-3.5 w-3.5" /> Illuminate in MoB
                        </button>
                      </div>
                      <div className="grid gap-3 p-4 md:grid-cols-3">
                        <div className="rounded-lg border border-border bg-card p-3"><div className="text-[8px] font-bold uppercase text-muted-foreground">Mission entities</div><div className="mt-1 text-lg font-black text-cyan-700 dark:text-cyan-200">{workspace.affectedEntities.length}</div></div>
                        <div className="rounded-lg border border-border bg-card p-3"><div className="text-[8px] font-bold uppercase text-muted-foreground">Linked FoB snapshot</div><div className="mt-1 truncate text-[10px] font-bold text-muted-foreground">{workspace.fobOverlay.snapshotId ?? 'MoB will use latest'}</div></div>
                        <div className="rounded-lg border border-border bg-card p-3"><div className="text-[8px] font-bold uppercase text-muted-foreground">Relationship policy</div><div className="mt-1 text-[10px] font-bold text-emerald-300">Observed/declarative only</div></div>
                      </div>
                    </section>
                  )}
                </div>
              )}
            </section>
          )}

          <section className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground"><Sparkles className="h-4 w-4 text-violet-300" /> AI C2 conversation</div>
              <div className="flex items-center gap-2">
                {canCapability('ai_command_control', 'export_mission') && selectedMission && status?.stage2CExportsEnabled !== false && (
                  <>
                    <button type="button" onClick={() => void downloadMissionExport('md', 'conversation')} className="inline-flex items-center gap-1 rounded-lg border border-cyan-500/30 bg-cyan-500/8 px-2 py-1 text-[9px] font-bold text-cyan-700 dark:text-cyan-200 hover:bg-cyan-500/15" title="Download persisted AI C2 conversation input/output as Markdown"><Download className="h-3 w-3" /> .MD</button>
                    <button type="button" onClick={() => void downloadMissionExport('txt', 'conversation')} className="inline-flex items-center gap-1 rounded-lg border border-slate-600 bg-muted px-2 py-1 text-[9px] font-bold text-foreground hover:bg-slate-700/70" title="Download persisted AI C2 conversation input/output as plain text"><FileText className="h-3 w-3" /> .TXT</button>
                  </>
                )}
                {(selectedMission?.status || stream.status !== 'idle') && <span className={`rounded-full border px-2 py-1 text-[9px] font-bold uppercase ${statusClass(stream.status !== 'idle' ? stream.status : selectedMission?.status ?? '')}`}>{stream.status !== 'idle' ? stream.status : selectedMission?.status}</span>}
                {detailLoading && <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            </div>
            {(stream.status === 'streaming' || selectedMission) && (missionMode === 'autonomous' || selectedMission?.missionMode === 'autonomous') && (
              <div className="border-b border-border bg-card px-4 py-3">
                <div className="mb-2 flex items-center justify-between gap-3"><div><div className="text-[10px] font-bold uppercase tracking-wider text-cyan-300">Mission completion</div><div className="mt-0.5 text-xs font-semibold text-foreground">{currentProgress.phase}</div></div><div className="text-lg font-black tabular-nums text-cyan-700 dark:text-cyan-200">{Math.round(currentProgress.percent)}%</div></div>
                <Progress value={currentProgress.percent} className="h-2 bg-muted [&>div]:bg-cyan-400" />
                {currentProgress.detail && <div className="mt-2 text-[10px] leading-4 text-muted-foreground">{currentProgress.detail}</div>}
              </div>
            )}
            <div className="max-h-[720px] min-h-[420px] space-y-4 overflow-y-auto p-4">
              {!selectedMission && stream.status === 'idle' && (
                <div className="flex min-h-[360px] flex-col items-center justify-center text-center">
                  <BrainCircuit className="mb-3 h-10 w-10 text-slate-700" />
                  <div className="text-sm font-semibold text-muted-foreground">AI C2 is standing by</div>
                  <div className="mt-1 max-w-lg text-xs text-muted-foreground">Select a customer, choose an operating level and analysis depth profile, then give the Foundry agent a detailed mission.</div>
                </div>
              )}

              {visibleMessages.map((message) => (
                <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[92%] rounded-xl border px-4 py-3 ${message.role === 'user' ? 'border-cyan-500/25 bg-cyan-500/8' : 'border-border bg-card'}`}>
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {message.role === 'user' ? <UserRound className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5 text-violet-300" />}
                      {message.role === 'user' ? 'Analyst' : 'AI C2'}
                    </div>
                    <div className="prose prose-sm prose-invert max-w-none break-words prose-p:my-2 prose-li:my-0.5 prose-pre:border prose-pre:border-border prose-pre:bg-card"><ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown></div>
                  </div>
                </div>
              ))}

              {stream.status !== 'idle' && stream.output && (
                <div className="flex justify-start">
                  <div className="max-w-[92%] rounded-xl border border-violet-500/25 bg-violet-500/7 px-4 py-3">
                    <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-violet-300"><Bot className="h-3.5 w-3.5" /> AI C2 live response</div>
                    <div className="prose prose-sm prose-invert max-w-none break-words prose-p:my-2 prose-li:my-0.5 prose-pre:border prose-pre:border-border prose-pre:bg-card"><ReactMarkdown remarkPlugins={[remarkGfm]}>{stream.output}</ReactMarkdown></div>
                    {stream.status === 'streaming' && <span className="mt-2 inline-flex items-center gap-1 text-[9px] text-cyan-300"><LoaderCircle className="h-3 w-3 animate-spin" /> streaming</span>}
                  </div>
                </div>
              )}
              {stream.error && <div className="rounded-xl border border-rose-500/30 bg-rose-500/8 p-3 text-xs text-rose-200"><XCircle className="mr-2 inline h-4 w-4" />{stream.error}</div>}
              <div ref={outputEndRef} />
            </div>

            {canContinue && selectedMission && (
              <div className="border-t border-border bg-card p-3">
                <div className="flex gap-2">
                  <textarea value={followUp} onChange={(event) => setFollowUp(event.target.value)} placeholder="Continue this mission thread…" className="min-h-[72px] flex-1 resize-y rounded-lg border border-border bg-card p-2.5 text-xs text-foreground outline-none focus:border-violet-500" />
                  <button type="button" disabled={!followUp.trim() || stream.status === 'streaming'} onClick={() => void continueMission()} className="self-end rounded-lg border border-violet-500/40 bg-violet-500/10 p-3 text-violet-700 dark:text-violet-200 hover:bg-violet-500/15 disabled:opacity-40" title="Continue thread"><Send className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </section>
        </main>

        <aside className="space-y-4">
          {canCapability('ai_command_control', 'view_metrics') && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between gap-2"><div className="flex items-center gap-2 text-sm font-bold text-foreground"><Gauge className="h-4 w-4 text-cyan-300" /> Mission telemetry</div><div className="flex items-center gap-1.5">{currentMetrics?.degradedCompletion && <span className="rounded-full border border-amber-500/30 bg-amber-500/8 px-2 py-1 text-[8px] font-bold uppercase text-amber-300">Evidence gaps</span>}{currentMetrics?.finalTier && <span className="rounded-full border border-violet-500/30 bg-violet-500/8 px-2 py-1 text-[8px] font-bold uppercase text-violet-300">Final · {currentMetrics.finalTier}</span>}</div></div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['Input tokens', currentMetrics?.inputTokens ?? 0],
                  ['Cached input', currentMetrics?.cachedInputTokens ?? 0],
                  ['Output tokens', currentMetrics?.outputTokens ?? 0],
                  ['Total tokens', currentMetrics?.totalTokens ?? 0],
                  ['Est. cost', currentMetrics?.estimatedCostUsd != null ? `$${currentMetrics.estimatedCostUsd.toFixed(4)}` : '$0.0000'],
                  ['Latency', currentMetrics?.latencyMs ? `${(currentMetrics.latencyMs / 1000).toFixed(1)}s` : '-'],
                  ['Tool calls', currentMetrics?.toolCalls ?? 0],
                  ['Agent calls', currentMetrics?.agentCalls ?? 0],
                  ['429 / retries', currentMetrics?.retries ?? 0],
                  ['Compactions', currentMetrics?.compactions ?? 0],
                  ['Schema repairs', currentMetrics?.schemaRepairs ?? 0],
                  ['Duplicate skips', currentMetrics?.duplicateToolSuppressions ?? 0],
                  ['Source gaps', currentMetrics?.sourceCapabilitiesDisabled ?? 0],
                  ['Worker checkpoints', currentMetrics?.workerCheckpoints ?? 0],
                  ['Ledger pages', currentMetrics?.evidenceLedgerPages ?? 0],
                  ['Ledger rows', currentMetrics?.evidenceLedgerRows ?? 0],
                  ['Ledger truncations', currentMetrics?.evidenceLedgerTruncations ?? 0],
                  ['Schema cache hits', currentMetrics?.schemaCacheHits ?? 0],
                  ['Compliance repairs', currentMetrics?.complianceRepairs ?? 0],
                  ['Tool failures', currentMetrics?.toolFailures ?? 0],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-lg border border-border bg-card p-2">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="mt-1 text-base font-bold text-foreground">{value}</div>
                  </div>
                ))}
              </div>
              {(currentMetrics?.tiers?.length ?? 0) > 0 && (
                <div className="mt-3 space-y-1.5">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Per-tier tokens / estimated cost</div>
                  {currentMetrics?.tiers?.map((tier) => (
                    <div key={tier.tier} className="rounded-lg border border-border bg-card p-2">
                      <div className="flex items-center justify-between gap-2"><div><div className="text-[10px] font-semibold text-foreground">{tier.label ?? tier.tier}</div><div className="text-[8px] text-muted-foreground">{tier.model ?? 'model'} · {tier.calls ?? 0} call(s){tier.retries ? ` · ${tier.retries} retries` : ''}</div></div><div className="text-[11px] font-bold text-emerald-300">${Number(tier.estimatedUsd ?? 0).toFixed(4)}</div></div>
                      <div className="mt-1 grid grid-cols-3 gap-1 text-[8px] text-muted-foreground"><span>IN {tier.inputTokens ?? 0}</span><span>CACHE {tier.cachedInputTokens ?? 0}</span><span>OUT {tier.outputTokens ?? 0}</span></div>
                    </div>
                  ))}
                  <div className="pt-1 text-[8px] leading-3 text-muted-foreground">Cost is an estimate from Foundry-reported token usage and configured per-tier rates. Azure Cost Management / billing meters remain authoritative.</div>
                </div>
              )}
            </section>
          )}

          {canCapability('ai_command_control', 'view_agent_activity') && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><Activity className="h-4 w-4 text-violet-300" /> Agent activity</div>
              <div className="max-h-[330px] space-y-3 overflow-y-auto pr-1">
                {currentEvents.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">Activity appears here during a mission.</div>}
                {currentEvents.map((event, index) => (
                  <div key={event.id ?? `${event.label}-${index}`} className="relative border-l border-border pl-3">
                    <span className="absolute -left-1 top-1 h-2 w-2 rounded-full bg-cyan-400" />
                    <div className="text-[11px] font-semibold text-foreground">{event.label}</div>
                    {event.detail && <div className="mt-0.5 text-[10px] leading-4 text-muted-foreground">{event.detail}</div>}
                    <div className="mt-1 text-[9px] text-slate-700">{formatDate(event.createdAt)}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {canCapability('ai_command_control', 'view_tool_activity') && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><Wrench className="h-4 w-4 text-amber-300" /> Read-only tool activity</div>
              <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                {currentToolCalls.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">Stage 2B.3 tool calls will appear here.</div>}
                {currentToolCalls.map((call) => (
                  <details key={call.id} className="rounded-lg border border-border bg-card p-2.5">
                    <summary className="cursor-pointer list-none">
                      <div className="flex items-start justify-between gap-2"><div><div className="text-[11px] font-semibold text-foreground">{call.toolName}</div><div className="mt-0.5 text-[9px] text-muted-foreground">{call.reason ?? call.callRef}</div></div><span className={`rounded border px-1.5 py-0.5 text-[8px] uppercase ${call.status === 'completed' ? 'border-emerald-500/30 text-emerald-300' : call.status === 'failed' ? 'border-rose-500/30 text-rose-300' : 'border-amber-500/30 text-amber-300'}`}>{call.status}</span></div>
                    </summary>
                    <div className="mt-2 text-[9px] text-muted-foreground">{call.durationMs != null ? `${call.durationMs} ms` : 'pending'} · {formatDate(call.requestedAt)}</div>
                    <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap break-all rounded border border-border bg-black/30 p-2 text-[9px] leading-4 text-muted-foreground">{prettyJson({ arguments: call.arguments, result: call.resultPreview ?? call.resultPayload })}</pre>
                  </details>
                ))}
              </div>
            </section>
          )}

          {canCapability('ai_command_control', 'view_evidence') && (
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><Database className="h-4 w-4 text-emerald-300" /> Evidence ledger</div>
              <div className="max-h-[430px] space-y-2 overflow-y-auto pr-1">
                {currentEvidence.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">FOB and read-only tool evidence attached to the mission will appear here.</div>}
                {currentEvidence.map((evidence) => (
                  <details key={evidence.id} className="rounded-lg border border-border bg-card p-2.5">
                    <summary className="cursor-pointer list-none">
                      <div className="flex items-start gap-2">
                        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" />
                        <div className="min-w-0"><div className="truncate text-[11px] font-semibold text-foreground">{evidence.label}</div><div className="text-[9px] text-muted-foreground">{evidence.source} · {evidence.evidenceType}</div></div>
                      </div>
                    </summary>
                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded border border-border bg-black/30 p-2 text-[9px] leading-4 text-muted-foreground">{prettyJson(evidence.payload)}</pre>
                  </details>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>

      <section className="rounded-2xl border border-border bg-card px-4 py-3 text-[10px] text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Foundry access uses VM Managed Identity - no Foundry API key/client secret.</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-cyan-400" /> Customer scope, tool permissions and KQL safety policy are enforced server-side.</span>
          <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5 text-violet-400" /> Messages, activity, evidence, metrics and mission state persist to PostgreSQL for audit/replay.</span>
        </div>
      </section>
    </div>
  );
}
